/**
 * Firebase Auth provider flows: anonymous, Google, and anonymous -> Google.
 *
 * The Firebase uid remains the only application identity. Nothing here creates
 * a parallel user system, and no provider grants a party role.
 *
 * Google authorization for YouTube is deliberately NOT requested here; see
 * services/youtubeAuth.js. Basic sign-in asks for profile scopes only.
 */
import {
  GoogleAuthProvider,
  getRedirectResult,
  linkWithPopup,
  linkWithRedirect,
  onAuthStateChanged,
  signInWithCredential,
  signInWithPopup,
  signInWithRedirect,
  signOut,
} from 'firebase/auth';
import { auth, ensureAnonymousAuth, resetAuthCache } from '../firebase';
import {
  LINK_OUTCOME,
  classifyAuthError,
  hasGoogleProvider,
  shouldLinkAnonymousUser,
} from '../utils/authIdentity';
import {
  clearGoogleRedirectContext,
  loadGoogleRedirectContext,
  saveGoogleRedirectContext,
} from '../utils/googleRedirect';

const COLLISION_RECOVERY_FAILED_MESSAGE =
  'That Google account is already set up on Rhythmé, but we could not switch to it. Please try again.';

export function createGoogleProvider({ scopes = [], customParameters = null } = {}) {
  const provider = new GoogleAuthProvider();
  scopes.forEach((scope) => provider.addScope(scope));
  if (customParameters) provider.setCustomParameters(customParameters);
  return provider;
}

export function observeAuthState(callback) {
  return onAuthStateChanged(auth, callback);
}

export function getCurrentUser() {
  return auth.currentUser ?? null;
}

/** Anonymous guest access. Unchanged Phase 1/2 behavior. */
export async function ensureGuestAuth() {
  return ensureAnonymousAuth();
}

function createSignInProvider() {
  return createGoogleProvider({
    customParameters: { prompt: 'select_account' },
  });
}

function credentialFromLinkError(error) {
  try {
    return GoogleAuthProvider.credentialFromError(error) || null;
  } catch {
    return null;
  }
}

/**
 * A failed collision recovery is never a user cancel. The original Google
 * popup already completed; swallowing `popup-closed-by-user` here left the
 * anonymous landing page unchanged after a successful Google chooser.
 */
function classifyCollisionRecoveryError(error) {
  const classified = classifyAuthError(error);
  if (classified.kind === 'cancelled') {
    return {
      kind: 'credential-recovery-failed',
      recoverable: true,
      strategy: 'report',
      message: COLLISION_RECOVERY_FAILED_MESSAGE,
    };
  }
  return classified;
}

async function signInWithExistingCredential(credential) {
  const result = await signInWithCredential(auth, credential);
  resetAuthCache();
  return result.user;
}

async function signInToExistingGoogleAccount(provider) {
  const result = await signInWithPopup(auth, provider);
  resetAuthCache();
  return result.user;
}

async function startGoogleRedirect({
  current,
  provider,
  previousUid,
  context,
  forceSignIn = false,
}) {
  saveGoogleRedirectContext({
    previousUid,
    eventCode: context?.eventCode || null,
    returnPath: context?.returnPath || (typeof window !== 'undefined' ? window.location.pathname : '/'),
  });

  try {
    // After a known uid collision, linking cannot succeed. Sign in to the
    // existing Google account instead of repeating linkWithRedirect.
    if (!forceSignIn && current && shouldLinkAnonymousUser(current)) {
      await linkWithRedirect(current, provider);
    } else {
      await signInWithRedirect(auth, provider);
    }
    return { outcome: LINK_OUTCOME.redirectStarted, user: current, previousUid };
  } catch (error) {
    clearGoogleRedirectContext();
    return {
      outcome: LINK_OUTCOME.failed,
      user: current,
      previousUid,
      error: classifyAuthError(error),
    };
  }
}

function shouldRetryWithRedirect(classified) {
  return classified?.strategy === 'retry-with-redirect';
}

async function finishCollisionRecoveryFailure({
  signInError,
  current,
  provider,
  previousUid,
  context,
}) {
  const signInClassified = classifyCollisionRecoveryError(signInError);
  if (shouldRetryWithRedirect(signInClassified)) {
    return startGoogleRedirect({
      current,
      provider,
      previousUid,
      context,
      forceSignIn: true,
    });
  }
  return {
    outcome: LINK_OUTCOME.failed,
    user: current,
    previousUid,
    error: signInClassified,
  };
}

/**
 * Continue after Firebase rejected an anonymous link because the Google
 * credential already belongs to another uid.
 *
 * Prefer the credential attached to that error so we do not open a second
 * popup. A second popup is only a fallback when the credential is missing.
 */
async function recoverExistingGoogleAccount({
  error,
  current,
  provider,
  previousUid,
  context,
}) {
  const classified = classifyAuthError(error);
  const credential = credentialFromLinkError(error);

  if (credential) {
    try {
      const user = await signInWithExistingCredential(credential);
      return {
        outcome: LINK_OUTCOME.signedInInstead,
        user,
        previousUid,
        error: classified,
      };
    } catch (signInError) {
      return finishCollisionRecoveryFailure({
        signInError,
        current,
        provider,
        previousUid,
        context,
      });
    }
  }

  try {
    const user = await signInToExistingGoogleAccount(provider);
    return {
      outcome: LINK_OUTCOME.signedInInstead,
      user,
      previousUid,
      error: classified,
    };
  } catch (signInError) {
    return finishCollisionRecoveryFailure({
      signInError,
      current,
      provider,
      previousUid,
      context,
    });
  }
}

/**
 * Finish a redirect that began because popup auth was blocked.
 *
 * Returns null when this page load was not a redirect return. A second hop is
 * used only when linking failed because the Google account already has a uid
 * and the failed result did not include a reusable credential.
 */
export async function completePendingGoogleRedirect() {
  const pending = loadGoogleRedirectContext();

  try {
    const result = await getRedirectResult(auth);
    if (!result?.user) return null;

    clearGoogleRedirectContext();
    resetAuthCache();

    const previousUid = pending?.previousUid || null;
    const outcome = previousUid && result.user.uid === previousUid
      ? LINK_OUTCOME.linked
      : LINK_OUTCOME.signedInInstead;

    return { outcome, user: result.user, previousUid, pending };
  } catch (error) {
    const classified = classifyAuthError(error);

    if (classified.strategy === 'sign-in-to-existing-account' && pending) {
      const credential = credentialFromLinkError(error);
      if (credential) {
        try {
          const user = await signInWithExistingCredential(credential);
          clearGoogleRedirectContext();
          return {
            outcome: LINK_OUTCOME.signedInInstead,
            user,
            previousUid: pending.previousUid,
            pending,
            error: classified,
          };
        } catch (signInError) {
          clearGoogleRedirectContext();
          return {
            outcome: LINK_OUTCOME.failed,
            user: getCurrentUser(),
            previousUid: pending.previousUid,
            pending,
            error: classifyCollisionRecoveryError(signInError),
          };
        }
      }

      try {
        await signInWithRedirect(auth, createSignInProvider());
        return {
          outcome: LINK_OUTCOME.redirectStarted,
          user: getCurrentUser(),
          previousUid: pending.previousUid,
          pending,
        };
      } catch (signInError) {
        clearGoogleRedirectContext();
        return {
          outcome: LINK_OUTCOME.failed,
          user: getCurrentUser(),
          previousUid: pending.previousUid,
          pending,
          error: classifyCollisionRecoveryError(signInError),
        };
      }
    }

    if (pending) clearGoogleRedirectContext();
    if (!pending) return null;
    return {
      outcome: classified.kind === 'cancelled' ? LINK_OUTCOME.cancelled : LINK_OUTCOME.failed,
      user: getCurrentUser(),
      previousUid: pending.previousUid,
      pending,
      error: classified,
    };
  }
}

/**
 * "Continue with Google".
 *
 * - already Google  -> no-op
 * - anonymous       -> link, preserving the uid and every party membership
 * - link conflict   -> sign in to the Google account that already exists
 * - cancelled popup -> reported, current session untouched
 * - blocked popup   -> Firebase redirect fallback (never for an intentional cancel)
 *
 * Returns `{ outcome, user, previousUid, error }`. Never throws for the
 * expected cases so callers can keep the existing session on failure.
 */
export async function continueWithGoogle({ context = null } = {}) {
  const provider = createSignInProvider();

  let current = getCurrentUser();
  if (!current) {
    try {
      current = await ensureGuestAuth();
    } catch {
      current = null;
    }
  }

  if (current && hasGoogleProvider(current)) {
    return { outcome: LINK_OUTCOME.alreadyGoogle, user: current, previousUid: current.uid };
  }

  const previousUid = current?.uid ?? null;

  if (current && shouldLinkAnonymousUser(current)) {
    try {
      const result = await linkWithPopup(current, provider);
      resetAuthCache();
      return { outcome: LINK_OUTCOME.linked, user: result.user, previousUid };
    } catch (error) {
      const classified = classifyAuthError(error);

      if (classified.strategy === 'sign-in-to-existing-account') {
        return recoverExistingGoogleAccount({
          error,
          current,
          provider,
          previousUid,
          context,
        });
      }

      if (classified.kind === 'cancelled') {
        return { outcome: LINK_OUTCOME.cancelled, user: current, previousUid, error: classified };
      }

      if (shouldRetryWithRedirect(classified)) {
        return startGoogleRedirect({ current, provider, previousUid, context });
      }

      return { outcome: LINK_OUTCOME.failed, user: current, previousUid, error: classified };
    }
  }

  try {
    const user = await signInToExistingGoogleAccount(provider);
    return { outcome: LINK_OUTCOME.signedInInstead, user, previousUid };
  } catch (error) {
    const classified = classifyAuthError(error);
    if (classified.kind === 'cancelled') {
      return { outcome: LINK_OUTCOME.cancelled, user: current, previousUid, error: classified };
    }
    if (shouldRetryWithRedirect(classified)) {
      return startGoogleRedirect({ current, provider, previousUid, context });
    }
    return { outcome: LINK_OUTCOME.failed, user: current, previousUid, error: classified };
  }
}

export async function signOutCurrentUser() {
  await signOut(auth);
  resetAuthCache();
}
